import React from 'react';
import _ from 'lodash';
import { useApolloClient } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Box } from '@dutchie/common/components/rebass-compat';

import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useErnie from 'shared/hooks/use-ernie';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useTurnstile } from 'src/components/turnstile';
import { TURNSTILE_ACTIONS, TurnstileAction } from 'shared/constants/turnstile';
import { getLogger } from 'src/utils/datadog-logger';
import { EnrollmentSteps, LOG_NAMESPACES } from 'src/payments/constants';
import { consumerSignup } from 'src/state/actions/users';
import { useTrackEnrollmentStepViewed } from 'src/payments/hooks/use-dutchie-pay-analytics/use-track-enrollment-step-viewed';
import { DutchiePayHeader, DutchiePayTextBody } from 'src/payments/components';
import { SignUpForm, SignUpFormProps } from './sign-up-form';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

export type SignUpFormWrapperProps = {
  titleText: string;
  subtitleText: string;
};

export function SignUpFormWrapper({ titleText, subtitleText }: SignUpFormWrapperProps): JSX.Element {
  const UI = useUI();
  const Cart = useCart();
  const apolloClient = useApolloClient();
  const { dispensary } = useDispensary();
  const showErnie = useErnie();
  const User = useUser();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const signUpFormRef = useTrackEnrollmentStepViewed({ step: EnrollmentSteps.signUpForm });

  const flags = useFlags();
  const isTurnstileEnabled = flags['growth.ecomm.signup-turnstile-enable.rollout'] ?? false;

  const { TurnstileComponent, executeAndWaitForToken } = useTurnstile({
    size: 'flexible',
    action: TURNSTILE_ACTIONS.SIGNUP as TurnstileAction,
  });

  const getCommonLogContext = (): Record<string, string> => ({
    step: 'signUpForm',
    userId: User?.id ?? 'unknown',
    dispensaryId: dispensary?.id ?? 'unknown',
  });

  const handleFormSubmit = async ({
    firstName,
    lastName,
    password,
    phoneNumber,
    birthday,
    medicalCardNumber,
    expirationDate,
    state,
  }: SignUpFormProps): Promise<void> => {
    try {
      let turnstileToken = '';
      if (isTurnstileEnabled) {
        try {
          turnstileToken = await executeAndWaitForToken();
        } catch (error) {
          throw Object.assign(new Error('Turnstile validation failed', { cause: error }), {
            ernieMessage: 'Error validating your request. Please try again.',
          });
        }
      }

      const profile = {
        email: DutchiePayEnrollment.userEmail,
        password,
        profile: {
          firstName: _.trim(firstName),
          lastName: _.trim(lastName),
          phone: phoneNumber,
          birthday,
          textNotifications: true,
          emailNotifications: true,
          medicalCard: state
            ? {
                number: state !== 'CO' ? medicalCardNumber : '',
                expirationDate,
                state,
              }
            : null,
        },
      };

      const userCreationData = {
        dispensaryId: dispensary?.id,
        embedded: UI.isEmbedded || UI.isStoreFront,
      };
      const { success, error } = await consumerSignup(
        apolloClient,
        User,
        Cart,
        UI,
        profile,
        userCreationData,
        turnstileToken
      );

      if (success) {
        DutchiePayEnrollment.signUpStepCompleted();
      } else if (error?.message) {
        throw new Error(error.message);
      } else {
        throw Object.assign(new Error('Unknown error during PBB signup'), {
          ernieMessage: 'We are unable to create your account. Please contact support.',
        });
      }
    } catch (err) {
      logger.error('Signup form submission error', {
        error: err,
        errorMessage: err?.message ?? 'Unknown error',
        cause: err?.cause ?? 'Unknown cause',
        errorCode: err?.code ?? 'NO_CODE',
        ...getCommonLogContext(),
      });
      console.error(err);
      const ernieMessage =
        err?.ernieMessage ?? err?.message ?? 'We are unable to create your account. Please contact support.';
      showErnie(ernieMessage, 'danger');
    }
  };

  const handleAgeCheck = (): void => undefined;

  return (
    <div ref={signUpFormRef}>
      <DutchiePayHeader>{titleText}</DutchiePayHeader>
      <DutchiePayTextBody>{subtitleText}</DutchiePayTextBody>
      <SignUpForm
        onFormSubmit={handleFormSubmit}
        showMedicalCardForm={false}
        userEmail={DutchiePayEnrollment.userEmail}
        ageCheck={handleAgeCheck}
      />
      {isTurnstileEnabled && (
        <Box mt='20px'>
          <TurnstileComponent />
        </Box>
      )}
    </div>
  );
}
